import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, Button, ButtonGroup, Spinner, Input } from 'reactstrap';
import {
  FaBoxOpen,
  FaCalendarAlt,
  FaChartBar,
  FaChartPie,
  FaExclamationTriangle,
  FaFileExcel,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaPercent,
  FaShoppingBasket,
  FaTrophy,
  FaUsers
} from 'react-icons/fa';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { saleActions } from '../../sagas/saleSlice';
import * as saleService from '../../services/saleService';
import { formatPrice } from '../../helpers/utility';

const CATEGORY_COLORS = ['#0f4c81', '#2c7da0', '#52b788', '#f4a261', '#bc6c25', '#6d597a', '#e76f51'];
const CATEGORY_CHART_LIMIT = 10;

const pad = (n) => String(n).padStart(2, '0');
const toISODate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const Dashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    totalVentes,
    totalAchats,
    totalAchatsImpayees,
    totalPaiementsFournisseur,
    lowStockItems,
    isLoading,
    nombreClients,
    panierMoyen,
    tauxRuptureStock,
    tauxAchatsUrgents,
    outOfStockItems,
    topProducts,
    slowMovingProducts,
    ventesParCategorie,
    ventesParClient
  } = useSelector((state) => state.sales);

  const [searchLowStock, setSearchLowStock] = useState('');
  const [lowStockPage, setLowStockPage] = useState(1);
  const lowStockPageSize = 8;
  const [marginPage, setMarginPage] = useState(1);
  const marginPageSize = 5;
  const [topProductsPage, setTopProductsPage] = useState(1);
  const [slowMovingPage, setSlowMovingPage] = useState(1);
  const [outOfStockPage, setOutOfStockPage] = useState(1);
  const [clientSalesPage, setClientSalesPage] = useState(1);
  const [salesPeriod, setSalesPeriod] = useState('today');
  const productTablePageSize = 8;

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const todayDate = useMemo(() => toISODate(today), [today]);
  const monthStartDate = useMemo(() => toISODate(new Date(today.getFullYear(), today.getMonth(), 1)), [today]);
  const [salesDateFilter, setSalesDateFilter] = useState({
    startDate: todayDate,
    endDate: todayDate
  });
  const salesDateRange = useMemo(() => {
    const startDate = salesDateFilter.startDate || todayDate;
    const endDate = salesDateFilter.endDate || startDate;
    const isToday = startDate === todayDate && endDate === todayDate;
    const isMonth = startDate === monthStartDate && endDate === todayDate;

    if (salesPeriod === 'today' || isToday) {
      return {
        startDate,
        endDate,
        label: "Aujourd'hui",
        revenueTitle: 'CA journalier'
      };
    }

    if (salesPeriod === 'month' || isMonth) {
      return {
        startDate,
        endDate,
        label: 'Ce mois',
        revenueTitle: 'CA mensuel'
      };
    }

    return {
      startDate,
      endDate,
      label: `Du ${startDate} au ${endDate}`,
      revenueTitle: 'CA periode'
    };
  }, [salesDateFilter, salesPeriod, monthStartDate, todayDate]);
  const isTodaySalesRange = salesDateRange.startDate === todayDate && salesDateRange.endDate === todayDate;
  const isMonthSalesRange = salesDateRange.startDate === monthStartDate && salesDateRange.endDate === todayDate;
  const [revenueChartLoading, setRevenueChartLoading] = useState(false);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);

  useEffect(() => {
    dispatch(saleActions.getSalesData({
      startDate: salesDateRange.startDate,
      endDate: salesDateRange.endDate
    }));
  }, [dispatch, salesDateRange]);

  useEffect(() => {
    let mounted = true;
    const loadRevenueCharts = async () => {
      setRevenueChartLoading(true);
      try {
        const [dailyResponse, monthlyResponse] = await Promise.all([
          saleService.getDailyRevenue(15),
          saleService.getMonthlyRevenue(6)
        ]);

        const dailySeries = (dailyResponse?.data || []).map((item) => ({
          label: item.label,
          date: item.period,
          revenue: Number(item.chiffreAffaires || 0)
        }));

        const monthlySeries = (monthlyResponse?.data || []).map((item) => ({
          label: item.label,
          month: item.period,
          revenue: Number(item.chiffreAffaires || 0)
        }));

        if (mounted) {
          setDailyRevenue(dailySeries);
          setMonthlyRevenue(monthlySeries);
        }
      } catch (error) {
        console.error('Erreur de chargement des graphiques CA:', error);
        if (mounted) {
          setDailyRevenue([]);
          setMonthlyRevenue([]);
        }
      } finally {
        if (mounted) {
          setRevenueChartLoading(false);
        }
      }
    };

    loadRevenueCharts();
    return () => {
      mounted = false;
    };
  }, []);

  const formatNumber = (value) => Number(value || 0).toLocaleString('fr-FR');
  const formatPercent = (value) => `${Number(value || 0).toFixed(2)} %`;
  const shortName = (value, size = 18) => {
    const text = String(value || 'N/A');
    return text.length > size ? `${text.slice(0, size)}...` : text;
  };

  const filteredLowStock = useMemo(() => {
    if (!searchLowStock) return lowStockItems || [];
    const q = searchLowStock.toLowerCase();

    return (lowStockItems || []).filter((item) => (
      (item.code || '').toLowerCase().includes(q) ||
      (item.categorieLibelle || '').toLowerCase().includes(q) ||
      (item.libelle || '').toLowerCase().includes(q)
    ));
  }, [lowStockItems, searchLowStock]);

  const lowStockTotalPages = Math.max(1, Math.ceil(filteredLowStock.length / lowStockPageSize));
  const paginatedLowStock = useMemo(() => {
    const start = (lowStockPage - 1) * lowStockPageSize;
    return filteredLowStock.slice(start, start + lowStockPageSize);
  }, [filteredLowStock, lowStockPage]);

  useEffect(() => {
    setLowStockPage(1);
  }, [searchLowStock, lowStockItems]);

  useEffect(() => {
    if (lowStockPage > lowStockTotalPages) {
      setLowStockPage(lowStockTotalPages);
    }
  }, [lowStockPage, lowStockTotalPages]);

  const categoryChartData = useMemo(() => (
    (ventesParCategorie || []).map((item) => ({
      name: item.categorieLibelle || 'Sans categorie',
      revenue: Number(item.chiffreAffaires || 0),
      quantity: Number(item.quantiteVendue || 0)
    }))
  ), [ventesParCategorie]);

  const sortedCategoryChartData = useMemo(() => (
    [...categoryChartData].sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0))
  ), [categoryChartData]);

  const categoryPieData = useMemo(() => {
    if (sortedCategoryChartData.length <= CATEGORY_CHART_LIMIT) {
      return sortedCategoryChartData;
    }

    const visibleCategories = sortedCategoryChartData.slice(0, CATEGORY_CHART_LIMIT);
    const otherCategories = sortedCategoryChartData.slice(CATEGORY_CHART_LIMIT);
    const otherTotals = otherCategories.reduce((totals, item) => ({
      revenue: totals.revenue + Number(item.revenue || 0),
      quantity: totals.quantity + Number(item.quantity || 0)
    }), { revenue: 0, quantity: 0 });

    return [
      ...visibleCategories,
      {
        name: `Autres (${otherCategories.length})`,
        revenue: otherTotals.revenue,
        quantity: otherTotals.quantity
      }
    ];
  }, [sortedCategoryChartData]);

  const categoryTotalRevenue = useMemo(() => (
    categoryChartData.reduce((total, item) => total + Number(item.revenue || 0), 0)
  ), [categoryChartData]);

  const topChartData = useMemo(() => (
    (topProducts || []).slice(0, 8).map((item) => ({
      name: shortName(item.articleLibelle, 20),
      quantity: Number(item.quantiteVendue || 0),
      revenue: Number(item.chiffreAffaires || 0)
    }))
  ), [topProducts]);

  const topProductNames = useMemo(() => {
    const names = (topProducts || [])
      .slice(0, 3)
      .map((item) => shortName(item.articleLibelle, 16))
      .filter(Boolean);

    return names.length > 0 ? names.join(', ') : 'Aucun produit';
  }, [topProducts]);

  const marginRows = useMemo(() => topProducts || [], [topProducts]);
  const marginTotalPages = Math.max(1, Math.ceil(marginRows.length / marginPageSize));
  const paginatedMarginRows = useMemo(() => {
    const start = (marginPage - 1) * marginPageSize;
    return marginRows.slice(start, start + marginPageSize);
  }, [marginPage, marginRows]);
  const marginTotals = useMemo(() => (
    marginRows.reduce((totals, item) => {
      const quantity = Number(item.quantiteVendue || 0);
      const purchaseCost = Number(item.coutAchat || 0) * quantity;

      return {
        quantity: totals.quantity + quantity,
        revenue: totals.revenue + Number(item.chiffreAffaires || 0),
        purchaseCost: totals.purchaseCost + purchaseCost,
        margin: totals.margin + Number(item.margeBrute || 0)
      };
    }, {
      quantity: 0,
      revenue: 0,
      purchaseCost: 0,
      margin: 0
    })
  ), [marginRows]);

  const topProductsRows = useMemo(() => topProducts || [], [topProducts]);
  const topProductsTotalPages = Math.max(1, Math.ceil(topProductsRows.length / productTablePageSize));
  const paginatedTopProducts = useMemo(() => {
    const start = (topProductsPage - 1) * productTablePageSize;
    return topProductsRows.slice(start, start + productTablePageSize);
  }, [topProductsPage, topProductsRows]);

  const slowMovingRows = useMemo(() => slowMovingProducts || [], [slowMovingProducts]);
  const slowMovingTotalPages = Math.max(1, Math.ceil(slowMovingRows.length / productTablePageSize));
  const paginatedSlowMoving = useMemo(() => {
    const start = (slowMovingPage - 1) * productTablePageSize;
    return slowMovingRows.slice(start, start + productTablePageSize);
  }, [slowMovingPage, slowMovingRows]);

  const outOfStockRows = useMemo(() => outOfStockItems || [], [outOfStockItems]);
  const outOfStockTotalPages = Math.max(1, Math.ceil(outOfStockRows.length / productTablePageSize));
  const paginatedOutOfStock = useMemo(() => {
    const start = (outOfStockPage - 1) * productTablePageSize;
    return outOfStockRows.slice(start, start + productTablePageSize);
  }, [outOfStockPage, outOfStockRows]);

  const clientSalesRows = useMemo(() => ventesParClient || [], [ventesParClient]);
  const clientSalesTotalPages = Math.max(1, Math.ceil(clientSalesRows.length / productTablePageSize));
  const paginatedClientSales = useMemo(() => {
    const start = (clientSalesPage - 1) * productTablePageSize;
    return clientSalesRows.slice(start, start + productTablePageSize);
  }, [clientSalesPage, clientSalesRows]);
  const clientSalesTotal = useMemo(() => (
    clientSalesRows.reduce((total, item) => total + Number(item.totalVentes || 0), 0)
  ), [clientSalesRows]);

  useEffect(() => {
    setMarginPage(1);
  }, [topProducts]);

  useEffect(() => {
    setTopProductsPage(1);
  }, [topProducts]);

  useEffect(() => {
    setSlowMovingPage(1);
  }, [slowMovingProducts]);

  useEffect(() => {
    setOutOfStockPage(1);
  }, [outOfStockItems]);

  useEffect(() => {
    setClientSalesPage(1);
  }, [ventesParClient]);

  useEffect(() => {
    if (marginPage > marginTotalPages) {
      setMarginPage(marginTotalPages);
    }
  }, [marginPage, marginTotalPages]);

  useEffect(() => {
    if (topProductsPage > topProductsTotalPages) {
      setTopProductsPage(topProductsTotalPages);
    }
  }, [topProductsPage, topProductsTotalPages]);

  useEffect(() => {
    if (slowMovingPage > slowMovingTotalPages) {
      setSlowMovingPage(slowMovingTotalPages);
    }
  }, [slowMovingPage, slowMovingTotalPages]);

  useEffect(() => {
    if (outOfStockPage > outOfStockTotalPages) {
      setOutOfStockPage(outOfStockTotalPages);
    }
  }, [outOfStockPage, outOfStockTotalPages]);

  useEffect(() => {
    if (clientSalesPage > clientSalesTotalPages) {
      setClientSalesPage(clientSalesTotalPages);
    }
  }, [clientSalesPage, clientSalesTotalPages]);

  const exportExcel = (rows, sheetName, filePrefix) => {
    if (!rows || rows.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Aucune donnee',
        text: 'Aucune donnee a exporter'
      });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const fileData = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    });

    saveAs(fileData, `${filePrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const mapProductRows = (rows) => (rows || []).map((item) => ({
    Produit: item.articleLibelle,
    Categorie: item.categorieLibelle,
    Quantite: Number(item.quantiteVendue || 0),
    CA: Number(item.chiffreAffaires || 0)
  }));

  const handleExportDailyRevenue = () => exportExcel(
    (dailyRevenue || []).map((item) => ({
      Date: item.date,
      Libelle: item.label,
      'Chiffre affaires': item.revenue
    })),
    'CA Journalier',
    'ca_journalier'
  );

  const handleExportMonthlyRevenue = () => exportExcel(
    (monthlyRevenue || []).map((item) => ({
      Mois: item.month,
      Libelle: item.label,
      'Chiffre affaires': item.revenue
    })),
    'CA Mensuel',
    'ca_mensuel'
  );

  const handleExportTopChart = () => exportExcel(mapProductRows(topProducts), 'Top Produits', 'top_produits');

  const handleExportCategorySales = () => exportExcel(
    (categoryChartData || []).map((item) => ({
      Categorie: item.name,
      Quantite: item.quantity,
      CA: item.revenue
    })),
    'Ventes Categorie',
    'ventes_par_categorie'
  );

  const handleExportClientSales = () => exportExcel(
    [
      ...((clientSalesRows || []).map((item) => ({
        Code: item.clientCode,
        Client: item.clientNom,
        'Nombre ventes': Number(item.nombreVentes || 0),
        'Total ventes': Number(item.totalVentes || 0)
      }))),
      {
        Code: '',
        Client: 'Total',
        'Nombre ventes': '',
        'Total ventes': clientSalesTotal
      }
    ],
    'Ventes Client',
    'ventes_par_client'
  );

  const handleExportMargin = () => exportExcel(
    [
      ...((marginRows || []).map((item) => {
        const quantity = Number(item.quantiteVendue || 0);
        return {
          Produit: item.articleLibelle,
          Categorie: item.categorieLibelle,
          Quantite: quantity,
          CA: Number(item.chiffreAffaires || 0),
          'Cout achat total': Number(item.coutAchat || 0) * quantity,
          'Marge brute': Number(item.margeBrute || 0)
        };
      })),
      {
        Produit: 'Total',
        Categorie: '',
        Quantite: marginTotals.quantity,
        CA: marginTotals.revenue,
        'Cout achat total': marginTotals.purchaseCost,
        'Marge brute': marginTotals.margin
      }
    ],
    'Marge Brute',
    'marge_brute'
  );

  const handleExportTopProductsTable = () => exportExcel(mapProductRows(topProducts), 'Produits Vendus', 'produits_les_plus_vendus');
  const handleExportSlowMoving = () => exportExcel(mapProductRows(slowMovingProducts), 'Faible Rotation', 'produits_faible_rotation');

  const handleExportOutOfStock = () => exportExcel(
    (outOfStockItems || []).map((item) => ({
      Code: item.code,
      Designation: item.libelle,
      Categorie: item.categorieLibelle,
      Stock: item.stockDisponible
    })),
    'Indisponibles',
    'produits_indisponibles'
  );

  const handleExportLowStockExcel = () => exportExcel(
    filteredLowStock.map((item) => ({
      Code: item.code,
      Categorie: item.categorieLibelle,
      Designation: item.libelle,
      'Stock minimal': item.stockMinimal,
      'Stock disponible': item.stockDisponible
    })),
    'Stock Faible',
    'stock_faible'
  );

  const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="d-flex align-items-center justify-content-between mb-3">
      <div className="d-flex align-items-center gap-2">
        <Icon className="text-primary" size={20} />
        <div>
          <h5 className="mb-0">{title}</h5>
          {subtitle ? <small className="text-muted">{subtitle}</small> : null}
        </div>
      </div>
      {isLoading ? <Spinner size="sm" color="primary" /> : null}
    </div>
  );

  const StatCard = ({ icon: Icon, title, value, color, formatter = formatPrice }) => (
    <Card className="shadow-sm h-100" style={{ minHeight: 88 }}>
      <CardBody className="py-2 px-3">
        <div className="d-flex align-items-center mb-2">
          <Icon className={`me-2 text-${color}`} size={16} />
          <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>{title}</span>
        </div>
        <h5 className="mb-0 fw-bold" style={{ fontSize: '1rem' }}>{formatter(value)}</h5>
      </CardBody>
    </Card>
  );

  const TextStatCard = ({ icon: Icon, title, value, color }) => (
    <Card className="shadow-sm h-100" style={{ minHeight: 88 }}>
      <CardBody className="py-2 px-3">
        <div className="d-flex align-items-center mb-2">
          <Icon className={`me-2 text-${color}`} size={16} />
          <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>{title}</span>
        </div>
        <div className="mb-0 fw-bold" style={{ fontSize: '0.9rem', lineHeight: 1.25 }}>
          {value}
        </div>
      </CardBody>
    </Card>
  );

  const ExportButton = ({ onClick }) => (
    <Button color="success" size="sm" onClick={onClick} title="Exporter vers Excel">
      <FaFileExcel className="me-1" />
      Excel
    </Button>
  );

  const PaginationControls = ({ totalItems, page, pageSize, totalPages, onPageChange, itemLabel = 'element' }) => (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
      <small className="text-muted">
        {totalItems === 0
          ? `0 ${itemLabel}`
          : `${((page - 1) * pageSize) + 1}-${Math.min(page * pageSize, totalItems)} sur ${totalItems}`}
      </small>

      <div className="d-flex align-items-center gap-2">
        <Button
          color="secondary"
          outline
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange((current) => Math.max(1, current - 1))}
        >
          Precedent
        </Button>
        <span className="small fw-semibold">
          Page {page} / {totalPages}
        </span>
        <Button
          color="secondary"
          outline
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange((current) => Math.min(totalPages, current + 1))}
        >
          Suivant
        </Button>
      </div>
    </div>
  );

  const ProductTable = ({ rows, emptyText = 'Aucune donnee', showMargin = false, totals = null }) => (
    <div className="table-responsive">
      <table className="table table-bordered table-sm table-hover mb-0">
        <thead className="table-light">
          <tr>
            <th>Produit</th>
            <th>Categorie</th>
            <th className="text-end">Qte</th>
            <th className="text-end">CA</th>
            {showMargin ? <th className="text-end">Cout achat</th> : null}
            {showMargin ? <th className="text-end">Marge brute</th> : null}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((item) => (
            <tr key={item.articleId}>
              <td>{item.articleLibelle}</td>
              <td>{item.categorieLibelle}</td>
              <td className="text-end">{Number(item.quantiteVendue || 0).toFixed(2)}</td>
              <td className="text-end">{formatPrice(item.chiffreAffaires || 0)}</td>
              {showMargin ? <td className="text-end">{formatPrice(Number(item.coutAchat || 0) * Number(item.quantiteVendue || 0))}</td> : null}
              {showMargin ? <td className="text-end">{formatPrice(item.margeBrute || 0)}</td> : null}
            </tr>
          ))}
          {(!rows || rows.length === 0) && (
            <tr>
              <td colSpan={showMargin ? 6 : 4} className="text-center text-muted py-3">{emptyText}</td>
            </tr>
          )}
        </tbody>
        {showMargin && totals ? (
          <tfoot className="table-light fw-bold">
            <tr>
              <td colSpan={2}>Total</td>
              <td className="text-end">{Number(totals.quantity || 0).toFixed(2)}</td>
              <td className="text-end">{formatPrice(totals.revenue || 0)}</td>
              <td className="text-end">{formatPrice(totals.purchaseCost || 0)}</td>
              <td className="text-end">{formatPrice(totals.margin || 0)}</td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-2">
                <FaChartPie className="text-primary" size={20} />
                <h4 className="mb-0">{t('dashboard.title')}</h4>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-3">
                <ButtonGroup size="sm">
                  <Button
                    color={isTodaySalesRange ? 'primary' : 'secondary'}
                    outline={!isTodaySalesRange}
                    onClick={() => {
                      setSalesPeriod('today');
                      setSalesDateFilter({
                        startDate: todayDate,
                        endDate: todayDate
                      });
                    }}
                  >
                    Aujourd'hui
                  </Button>
                  <Button
                    color={isMonthSalesRange ? 'primary' : 'secondary'}
                    outline={!isMonthSalesRange}
                    onClick={() => {
                      setSalesPeriod('month');
                      setSalesDateFilter({
                        startDate: monthStartDate,
                        endDate: todayDate
                      });
                    }}
                  >
                    Ce mois
                  </Button>
                </ButtonGroup>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Input
                    type="date"
                    bsSize="sm"
                    value={salesDateFilter.startDate}
                    max={salesDateFilter.endDate || undefined}
                    onChange={(event) => {
                      setSalesPeriod('custom');
                      setSalesDateFilter((prev) => ({
                        ...prev,
                        startDate: event.target.value
                      }));
                    }}
                    style={{ width: 145 }}
                  />
                  <span className="text-muted small">au</span>
                  <Input
                    type="date"
                    bsSize="sm"
                    value={salesDateFilter.endDate}
                    min={salesDateFilter.startDate || undefined}
                    onChange={(event) => {
                      setSalesPeriod('custom');
                      setSalesDateFilter((prev) => ({
                        ...prev,
                        endDate: event.target.value
                      }));
                    }}
                    style={{ width: 145 }}
                  />
                </div>
                <small className="text-muted">Vue: {salesDateRange.label} | CA 15 jours et CA mensuel 6 mois</small>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <section className="mb-4">
        <SectionHeader icon={FaChartBar} title="Ventes" subtitle="Chiffre d'affaires, clients, panier moyen et repartition par categorie." />
        <Row className="g-3 mb-3">
          <Col md="6" xl="3">
            <StatCard icon={FaCalendarAlt} title={salesDateRange.revenueTitle} value={totalVentes} color="success" />
          </Col>
          <Col md="6" xl="3">
            <StatCard icon={FaUsers} title="Nombre de clients" value={nombreClients} color="primary" formatter={formatNumber} />
          </Col>
          <Col md="6" xl="3">
            <StatCard icon={FaShoppingBasket} title="Panier moyen" value={panierMoyen} color="info" />
          </Col>
          <Col md="6" xl="3">
            <TextStatCard icon={FaTrophy} title="Top produits" value={topProductNames} color="warning" />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xl="6">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Chiffre d'affaires journalier</h6>
                  <div className="d-flex align-items-center gap-2">
                    {revenueChartLoading ? <Spinner size="sm" color="success" /> : null}
                    <ExportButton onClick={handleExportDailyRevenue} />
                  </div>
                </div>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <AreaChart data={dailyRevenue} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                      <defs>
                        <linearGradient id="dailyRevenueFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2c7da0" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#2c7da0" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={(value) => formatPrice(value, false)} />
                      <Tooltip formatter={(value) => [formatPrice(value), 'CA']} />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        name="CA journalier"
                        stroke="#2c7da0"
                        strokeWidth={3}
                        fill="url(#dailyRevenueFill)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Chiffre d'affaires mensuel</h6>
                  <div className="d-flex align-items-center gap-2">
                    {revenueChartLoading ? <Spinner size="sm" color="primary" /> : null}
                    <ExportButton onClick={handleExportMonthlyRevenue} />
                  </div>
                </div>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={monthlyRevenue} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={(value) => formatPrice(value, false)} />
                      <Tooltip formatter={(value) => [formatPrice(value), 'CA mensuel']} />
                      <Bar dataKey="revenue" name="CA mensuel" fill="#52b788" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col xl="7">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Top produits par quantite</h6>
                  <ExportButton onClick={handleExportTopChart} />
                </div>
                <div style={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={topChartData} layout="vertical" margin={{ top: 10, right: 20, left: 30, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip formatter={(value, name) => name === 'revenue' ? [formatPrice(value), 'CA'] : [value, 'Qte']} />
                      <Bar dataKey="quantity" fill="#e76f51" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="5">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-start justify-content-between mb-3 gap-2">
                  <div>
                    <h6 className="mb-0">Ventes par categorie</h6>
                    <small className="text-muted">
                      Top {Math.min(CATEGORY_CHART_LIMIT, sortedCategoryChartData.length)}
                      {sortedCategoryChartData.length > CATEGORY_CHART_LIMIT ? ' + autres' : ''}
                    </small>
                  </div>
                  <ExportButton onClick={handleExportCategorySales} />
                </div>

                <div className="row g-2 align-items-center">
                  <div className="col-md-6">
                    <div style={{ width: '100%', height: 260 }}>
                      <ResponsiveContainer>
                        <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                          <Pie
                            data={categoryPieData}
                            dataKey="revenue"
                            nameKey="name"
                            innerRadius="52%"
                            outerRadius="82%"
                            paddingAngle={2}
                          >
                            {categoryPieData.map((entry, index) => (
                              <Cell key={entry.name} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name, props) => [formatPrice(value), props?.payload?.name || 'CA']} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div style={{ maxHeight: 260, overflowY: 'auto', paddingRight: 4 }}>
                      {categoryPieData.map((item, index) => {
                        const percent = categoryTotalRevenue > 0
                          ? (Number(item.revenue || 0) / categoryTotalRevenue) * 100
                          : 0;

                        return (
                          <div key={item.name} className="d-flex align-items-start gap-2 py-1 border-bottom">
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: 2,
                                marginTop: 5,
                                flex: '0 0 auto',
                                background: CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                              }}
                            />
                            <div className="min-w-0 flex-grow-1">
                              <div className="fw-semibold text-truncate" title={item.name} style={{ fontSize: '0.82rem' }}>
                                {item.name}
                              </div>
                              <div className="d-flex justify-content-between gap-2 text-muted" style={{ fontSize: '0.75rem' }}>
                                <span>{formatPrice(item.revenue, false)}</span>
                                <span>{percent.toFixed(1)}%</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {categoryPieData.length === 0 && (
                        <div className="text-center text-muted py-4">Aucune categorie</div>
                      )}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col>
            <Card className="shadow-sm">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Total des ventes par client</h6>
                  <ExportButton onClick={handleExportClientSales} />
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Code</th>
                        <th>Client</th>
                        <th className="text-end">Nombre ventes</th>
                        <th className="text-end">Total ventes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedClientSales.map((item) => (
                        <tr key={item.clientId || item.clientCode || item.clientNom}>
                          <td>{item.clientCode || '-'}</td>
                          <td>{item.clientNom || 'Client'}</td>
                          <td className="text-end">{formatNumber(item.nombreVentes || 0)}</td>
                          <td className="text-end">{formatPrice(item.totalVentes || 0)}</td>
                        </tr>
                      ))}
                      {clientSalesRows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-3">Aucune vente client</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="table-light fw-bold">
                      <tr>
                        <td colSpan={3}>Total</td>
                        <td className="text-end">{formatPrice(clientSalesTotal)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <PaginationControls
                  totalItems={clientSalesRows.length}
                  page={clientSalesPage}
                  pageSize={productTablePageSize}
                  totalPages={clientSalesTotalPages}
                  onPageChange={setClientSalesPage}
                  itemLabel="client"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </section>

      <section className="mb-4">
        <SectionHeader icon={FaFileInvoiceDollar} title="Achat" subtitle="Couts, paiements, impayes, achats urgents et marge brute produit." />
        <Row className="g-3 mb-3">
          <Col md="3">
            <StatCard icon={FaFileInvoiceDollar} title="Total achats" value={totalAchats} color="primary" />
          </Col>
          <Col md="3">
            <StatCard icon={FaMoneyBillWave} title="Paiements fournisseur" value={totalPaiementsFournisseur} color="success" />
          </Col>
          <Col md="3">
            <StatCard icon={FaExclamationTriangle} title="Achats impayes" value={totalAchatsImpayees} color="danger" />
          </Col>
          <Col md="3">
            <StatCard icon={FaPercent} title="Achats urgents" value={tauxAchatsUrgents} color="warning" formatter={formatPercent} />
          </Col>
        </Row>

        <Row className="g-3">
          <Col>
            <Card className="shadow-sm">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Cout d'achat et marge brute par produit</h6>
                  <ExportButton onClick={handleExportMargin} />
                </div>
                <ProductTable rows={paginatedMarginRows} showMargin totals={marginTotals} />
                <PaginationControls
                  totalItems={marginRows.length}
                  page={marginPage}
                  pageSize={marginPageSize}
                  totalPages={marginTotalPages}
                  onPageChange={setMarginPage}
                  itemLabel="produit"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </section>

      <section className="mb-5">
        <SectionHeader icon={FaBoxOpen} title="Produits" subtitle="Ruptures, faible rotation, top produits et stock faible." />
        <Row className="g-3 mb-3">
          <Col md="3">
            <StatCard icon={FaExclamationTriangle} title="Alertes rupture" value={outOfStockItems?.length || 0} color="danger" formatter={formatNumber} />
          </Col>
          <Col md="3">
            <StatCard icon={FaPercent} title="Taux rupture" value={tauxRuptureStock} color="danger" formatter={formatPercent} />
          </Col>
          <Col md="3">
            <StatCard icon={FaBoxOpen} title="Stock faible" value={lowStockItems?.length || 0} color="warning" formatter={formatNumber} />
          </Col>
          <Col md="3">
            <StatCard icon={FaChartBar} title="Slow moving" value={slowMovingProducts?.length || 0} color="secondary" formatter={formatNumber} />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xl="6">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Produits les plus vendus</h6>
                  <ExportButton onClick={handleExportTopProductsTable} />
                </div>
                <ProductTable rows={paginatedTopProducts} />
                <PaginationControls
                  totalItems={topProductsRows.length}
                  page={topProductsPage}
                  pageSize={productTablePageSize}
                  totalPages={topProductsTotalPages}
                  onPageChange={setTopProductsPage}
                  itemLabel="produit"
                />
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Produits a faible rotation</h6>
                  <ExportButton onClick={handleExportSlowMoving} />
                </div>
                <ProductTable rows={paginatedSlowMoving} />
                <PaginationControls
                  totalItems={slowMovingRows.length}
                  page={slowMovingPage}
                  pageSize={productTablePageSize}
                  totalPages={slowMovingTotalPages}
                  onPageChange={setSlowMovingPage}
                  itemLabel="produit"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xl="6">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center">
                    <FaExclamationTriangle className="text-danger me-2" size={20} />
                    <h6 className="mb-0">Produits indisponibles</h6>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-bold text-danger">{formatPercent(tauxRuptureStock)}</span>
                    <ExportButton onClick={handleExportOutOfStock} />
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Code</th>
                        <th>Designation</th>
                        <th>Categorie</th>
                        <th className="text-end">Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedOutOfStock.map((item) => (
                        <tr key={item.id}>
                          <td>{item.code}</td>
                          <td>{item.libelle}</td>
                          <td>{item.categorieLibelle}</td>
                          <td className="text-end text-danger fw-bold">{item.stockDisponible}</td>
                        </tr>
                      ))}
                      {(!outOfStockItems || outOfStockItems.length === 0) && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-3">Aucune rupture critique</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <PaginationControls
                  totalItems={outOfStockRows.length}
                  page={outOfStockPage}
                  pageSize={productTablePageSize}
                  totalPages={outOfStockTotalPages}
                  onPageChange={setOutOfStockPage}
                  itemLabel="produit"
                />
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                  <div className="d-flex align-items-center">
                    <FaBoxOpen className="text-warning me-2" size={20} />
                    <h6 className="mb-0">{t('dashboard.lowStockAlert')}</h6>
                  </div>

                  <div className="d-flex gap-2 align-items-center">
                    <Input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchLowStock || ''}
                      onChange={(event) => setSearchLowStock(event.target.value)}
                      style={{ width: 180 }}
                    />
                    <Button color="success" size="sm" onClick={handleExportLowStockExcel} title="Exporter vers Excel">
                      <FaFileExcel className="me-1" />
                      Excel
                    </Button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-bordered table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Code</th>
                        <th>Categorie</th>
                        <th>Designation</th>
                        <th className="text-end">Min</th>
                        <th className="text-end">Disponible</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedLowStock.map((item) => (
                        <tr key={item.id}>
                          <td>{item.code}</td>
                          <td>{item.categorieLibelle}</td>
                          <td>{item.libelle}</td>
                          <td className="text-end">{item.stockMinimal}</td>
                          <td className="text-end text-danger fw-bold">{item.stockDisponible}</td>
                        </tr>
                      ))}
                      {filteredLowStock.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-3">Aucun article en stock faible</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <PaginationControls
                  totalItems={filteredLowStock.length}
                  page={lowStockPage}
                  pageSize={lowStockPageSize}
                  totalPages={lowStockTotalPages}
                  onPageChange={setLowStockPage}
                  itemLabel="article"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </section>
    </Container>
  );
};

export default Dashboard;
